import {
  NexusProduct,
  NexusPricingEntry,
  NexusAddon,
  NexusVariantGroup,
} from "@/types/nexus-product";
import {
  extractOrderNumber,
  parseVendureNameParts,
} from "@/utils/sortByOrderPrefix";

export type NexusOptionGroup = {
  groupId: string;
  groupName: string;
  groupDescription?: string;
  multiSelect: boolean;
  requiredCount: number | null;
  hasDisplayWhen: boolean;
  options: Array<{
    id: string;
    name: string;
    disabled: boolean;
  }>;
};

function getPrefixedVariantName(
  variantId: string,
  groupIndex: number,
  pricing: Record<string, NexusPricingEntry>
): string {
  const entry = Object.values(pricing).find((e) =>
    e.variantIds.includes(variantId)
  );
  if (!entry) return "";
  const parts = parseVendureNameParts(entry.vendureName);
  return parts[groupIndex] ?? "";
}

export function isNexusGroupVisible(
  group: NexusVariantGroup,
  selectedOptions: Record<string, string>
): boolean {
  if (!group.displayWhen) return true;
  return (
    selectedOptions[group.displayWhen.groupId] === group.displayWhen.variantId
  );
}

export function buildNexusOptionGroups(
  nexusProduct: NexusProduct,
  selectedOptions: Record<string, string>
): NexusOptionGroup[] {
  const singleSelectGroups = nexusProduct.variantGroups.filter(
    (group) => !group.multiSelect && !group.displayWhen
  );

  return nexusProduct.variantGroups
    .filter((group) => isNexusGroupVisible(group, selectedOptions))
    .map((group) => {
      if (group.multiSelect) {
        return {
          groupId: group.id,
          groupName: group.name,
          groupDescription: group.description || undefined,
          multiSelect: true,
          requiredCount: group.requiredCount ?? null,
          hasDisplayWhen: Boolean(group.displayWhen),
          options: group.variants.map((v) => ({
            id: v.id,
            name: v.name,
            disabled: v.disabled,
          })),
        };
      }

      const groupIndex = singleSelectGroups.indexOf(group);
      const variantsWithOrder = group.variants.map((variant) => ({
        ...variant,
        _prefixedName: getPrefixedVariantName(
          variant.id,
          groupIndex,
          nexusProduct.pricing
        ),
      }));

      const sorted = [...variantsWithOrder].sort((a, b) => {
        const orderA = extractOrderNumber(a._prefixedName);
        const orderB = extractOrderNumber(b._prefixedName);
        if (orderA !== Infinity && orderB !== Infinity) return orderA - orderB;
        if (orderA === Infinity && orderB === Infinity)
          return a.name.localeCompare(b.name);
        return orderA === Infinity ? 1 : -1;
      });

      return {
        groupId: group.id,
        groupName: group.name,
        groupDescription: group.description || undefined,
        multiSelect: false,
        requiredCount: null,
        hasDisplayWhen: Boolean(group.displayWhen),
        options: sorted.map((v) => ({
          id: v.id,
          name: v.name,
          disabled: v.disabled,
        })),
      };
    });
}

export function findNexusPricingEntry(
  nexusProduct: NexusProduct,
  selectedOptions: Record<string, string>,
  pricingGroupIds: string[]
): NexusPricingEntry | undefined {
  const relevantIds = pricingGroupIds
    .map((groupId) => selectedOptions[groupId])
    .filter((id): id is string => Boolean(id));
  if (relevantIds.length !== pricingGroupIds.length) return undefined;

  return Object.values(nexusProduct.pricing).find((entry) => {
    if (entry.disabled) return false;
    return (
      relevantIds.every((id) => entry.variantIds.includes(id)) &&
      entry.variantIds.length === pricingGroupIds.length
    );
  });
}

export function isNexusOptionAvailable(
  optionId: string,
  groupId: string,
  selectedOptions: Record<string, string>,
  nexusProduct: NexusProduct,
  pricingGroupIds: string[]
): boolean {
  return Object.values(nexusProduct.pricing).some((entry) => {
    if (entry.disabled) return false;
    if (!entry.variantIds.includes(optionId)) return false;
    return pricingGroupIds.every((gId) => {
      if (gId === groupId) return true;
      const vId = selectedOptions[gId];
      return vId ? entry.variantIds.includes(vId) : true;
    });
  });
}

export function getNexusBasePrice(nexusProduct: NexusProduct): number {
  const prices = Object.values(nexusProduct.pricing)
    .filter((e) => !e.disabled)
    .map((e) => e.price);
  return prices.length ? Math.min(...prices) / 100 : 0;
}

export function isNexusAddonVisible(
  addon: NexusAddon,
  selectedOptions: Record<string, string>
): boolean {
  return isNexusAddonAvailableForVariantIds(
    addon,
    Object.values(selectedOptions)
  );
}

export function isNexusAddonAvailableForVariantIds(
  addon: NexusAddon,
  variantIds: string[]
): boolean {
  if (!addon.conditions.length) return true;
  const selectedVariantIds = new Set(variantIds);
  return addon.conditions.some(
    (condition) =>
      condition.variantIds.length > 0 &&
      condition.variantIds.every((id) => selectedVariantIds.has(id))
  );
}

export function resolveAddonPrice(
  addon: NexusAddon,
  selectedOptions: Record<string, string>
): number {
  return resolveAddonPriceForVariantIds(addon, Object.values(selectedOptions));
}

export function resolveAddonPriceForVariantIds(
  addon: NexusAddon,
  variantIds: string[]
): number {
  const selectedVariantIds = new Set(variantIds);
  let bestPrice: number | null = null;
  let bestMatchCount = 0;

  for (const condition of addon.conditions) {
    if (!condition.variantIds.length) continue;
    const allMatch = condition.variantIds.every((id) =>
      selectedVariantIds.has(id)
    );
    if (allMatch && condition.variantIds.length > bestMatchCount) {
      bestPrice = condition.price;
      bestMatchCount = condition.variantIds.length;
    }
  }

  return bestPrice !== null ? bestPrice : addon.basePrice;
}
